'use client';
import {
	type ApiErrorResponseErrorItem,
	type StepperRef,
	useAppLoader,
	useIntl,
	usePrevious,
	type UserEmailVerificationRequirementConfig
} from '@capsicum/common';
import { useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import {
	useEmailAvailabilityCheck,
	useSendCodeVerificationToEmail,
	useSubmitEmailRequirementVerificationCode
} from '../data-access/queries/useEmailQueries';
import { type Requirement } from '../models/requirements';

export const useEmailVerificationRequirement = (requirement: Requirement<UserEmailVerificationRequirementConfig>) => {
	const { $t, locale } = useIntl();
	const appLoader = useAppLoader();
	const [currentStep, setCurrentStep] = useState(0);
	const [userEmail, setUserEmail] = useState(requirement.config.prefill.email || '');
	const [emailError, setEmailError] = useState<{ message: string; type: string } | undefined>(undefined);
	const [codeVerificationError, setCodeVerificationError] = useState<ApiErrorResponseErrorItem | null>(null);
	const stepperRef = useRef<StepperRef>(null);
	const previousStep = usePrevious(currentStep);

	const { config } = requirement;
	const readonly = !config.prefill.emailCanBeUpdated;

	const {
		data: checkEmailAvailabilityData,
		isLoading: useEmailAvailabilityCheckLoading,
		mutateAsync: checkEmailAvailability
	} = useEmailAvailabilityCheck();

	const debouncedCheckEmailAvailability = useDebouncedCallback(email => checkEmailAvailability({ email }), 750, {
		leading: false,
		trailing: true
	});

	const isCheckEmailAvailabilityLoading =
		useEmailAvailabilityCheckLoading || debouncedCheckEmailAvailability.isPending();

	const setUserEmailHandler = async (value: string) => {
		setUserEmail(value);

		await debouncedCheckEmailAvailability(value);
	};

	const {
		error: sendCodeToEmailErrors,
		isLoading: isSendCodeToEmailLoading,
		isSuccess: sendCodeToEmailSuccess,
		mutateAsync: sendCodeToEmailVerification
	} = useSendCodeVerificationToEmail();

	const {
		error: submitEmailVerificationCodeError,
		isSuccess: submitEmailVerificationCodeSuccess,
		mutateAsync: submitEmailVerificationCode
	} = useSubmitEmailRequirementVerificationCode();

	const onCodeSubmit = (code: string) =>
		appLoader(submitEmailVerificationCode({ email: userEmail, generatedCode: code }));

	const sendVerificationCodeToEmail = () => appLoader(sendCodeToEmailVerification({ email: userEmail }));

	const updateStep = (step: number) => {
		setCurrentStep(step);
	};

	const isVerifyEmailButtonDisabled = Boolean(emailError) || !userEmail || isCheckEmailAvailabilityLoading;

	const codeVerificationErrorMessage =
		(codeVerificationError?.translations as Record<string, string>)?.[locale] || codeVerificationError?.title;

	useEffect(() => {
		if (currentStep === 0 && sendCodeToEmailSuccess) {
			stepperRef.current?.goToNextStep();
		}
	}, [sendCodeToEmailSuccess]);

	useEffect(() => {
		if (sendCodeToEmailErrors) {
			const sendCodeToEmailError = sendCodeToEmailErrors?.response?.data?.errors?.[0];
			const sendCodeToEmailErrorMessage =
				(sendCodeToEmailError?.translations as Record<string, string>)?.[locale] || sendCodeToEmailError?.title;

			if (sendCodeToEmailErrorMessage) setEmailError({ message: sendCodeToEmailErrorMessage, type: 'invalid' });
		}
	}, [sendCodeToEmailErrors]);

	useEffect(() => {
		setEmailError(undefined);
		if (!checkEmailAvailabilityData?.data || isCheckEmailAvailabilityLoading) return;

		if (!checkEmailAvailabilityData.data.isValid) {
			setEmailError({ message: $t('registerEmail.invalidEmail'), type: 'invalid' });

			return;
		}

		if (
			!checkEmailAvailabilityData.data.isAvailable &&
			requirement.config.prefill.email?.toLowerCase() !== userEmail.toLowerCase()
		) {
			setEmailError({ message: $t('registerEmail.emailAlreadyInUse'), type: 'invalid' });
		}
	}, [checkEmailAvailabilityData?.data, userEmail]);

	useEffect(() => {
		const verificationError = submitEmailVerificationCodeError?.response?.data?.errors?.[0];

		if (verificationError) {
			setCodeVerificationError(verificationError);
		}
	}, [submitEmailVerificationCodeError]);

	useEffect(() => {
		if (currentStep === 0 && previousStep === 1) {
			setCodeVerificationError(null);
		}
	}, [currentStep]);

	return {
		codeVerificationErrorMessage,
		currentStep,
		emailError,
		isSendCodeToEmailLoading,
		isVerifyEmailButtonDisabled,
		onCodeSubmit,
		readonly,
		sendVerificationCodeToEmail,
		setUserEmailHandler,
		stepperRef,
		submitEmailVerificationCodeSuccess,
		updateStep,
		userEmail
	};
};
